import { useEffect, useId, useRef, useState, type FC } from "react";
import { Box, Button, ButtonBase, IconButton, Stack, TextField, Typography } from "@mui/material";
import { lighten, type SxProps, type Theme } from "@mui/material/styles";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import type {
  ClassificationCriticalityTable,
  ClassificationCustomerCustomizations,
  ClassificationTemplate,
} from "../api/classificationTemplate";
import { DynamicForm } from "../dynamicForm";
import { CLASSIFICATION_COLORS } from "./classificationColors";
import type { ClassificationPropertyKey, Criticality, Level, Score } from "./schemas";
import { HoverPopover, useHoverPopover } from "../shared/HoverPopover";
import { InfoIcon, XIcon } from "../shared/icons";

type Template = ClassificationTemplate;
type TemplateProperty = Template["properties"][number];
type TemplateLevel = NonNullable<Template["levels"]>[number];

type PropertyLevel = { propertyKey: ClassificationPropertyKey; level: Level };

type Props = {
  template: Template;
  customerCustomizations: ClassificationCustomerCustomizations;
  criticalityTable: ClassificationCriticalityTable;
  scores: Array<Score>;
  criticality?: Criticality | null;
  rationale?: string;
  isReadOnly?: boolean;
  onSave: (payload: { scores: Array<Score>; rationale: string }) => Promise<unknown>;
};

const useHoverPreview = (enabled: boolean) => {
  const [preview, setPreview] = useState<PropertyLevel | null>(null);
  const suppressedRef = useRef(false);

  /*
   * Without this, clicking a cell to enter edit mode also paints the hover
   * preview on it, which reads as "I just selected that level" — but the
   * preview vanishes on the next mouse move. Suppress until first real move.
   */
  useEffect(() => {
    if (!enabled) return;
    const onMove = () => {
      suppressedRef.current = false;
    };
    window.addEventListener("pointermove", onMove, { once: true, passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [enabled]);

  const setPreviewUnlessSuppressed = (next: PropertyLevel | null) => {
    if (next !== null && suppressedRef.current) return;
    setPreview(next);
  };

  return {
    preview,
    setPreview: setPreviewUnlessSuppressed,
    setPreviewIgnoringSuppression: setPreview,
    suppressUntilNextPointerMove: () => {
      suppressedRef.current = true;
    },
  };
};

export const ClassificationCard: FC<Props> = ({
  template,
  customerCustomizations,
  criticalityTable,
  criticality,
  scores,
  rationale,
  isReadOnly = false,
  onSave,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Map<ClassificationPropertyKey, Level> | null>(null);
  const [draftRationale, setDraftRationale] = useState("");
  const [lastSetScore, setLastSetScore] = useState<PropertyLevel | null>(null);
  const updateMutation = useMutation({ mutationFn: onSave });

  const isEditing = draft !== null;
  const isInteractive = !isEditing && !isReadOnly;
  const hover = useHoverPreview(isEditing);
  const levelExplanationBaseId = useId();

  const levels = Array.from(
    { length: template.maxLevel - template.minLevel + 1 },
    (_, i) => (template.minLevel + i) as Level
  );

  const customerCustomizationByKey = new Map(
    customerCustomizations.propertyDescriptions.map(({ propertyKey, description }) => [propertyKey, description])
  );

  const onStartEdit = () => {
    updateMutation.reset();
    setDraft(new Map(scores.map(s => [s.propertyKey, s.value])));
    setDraftRationale(rationale ?? "");
    hover.suppressUntilNextPointerMove();
  };
  const leaveEditMode = () => {
    setDraft(null);
    hover.setPreview(null);
    setLastSetScore(null);
    setDraftRationale("");
  };
  const onCancel = () => {
    leaveEditMode();
    updateMutation.reset();
  };
  const handleSave = () => {
    if (!draft) return;
    const scores = Array.from(draft, ([propertyKey, value]) => ({ propertyKey, value }));
    updateMutation.mutate({ scores, rationale: draftRationale }, { onSuccess: leaveEditMode });
  };
  const onSetScore = (propertyKey: ClassificationPropertyKey, value: Level) => {
    const next = new Map(draft);
    next.set(propertyKey, value);
    setDraft(next);
    setLastSetScore({ propertyKey, level: value });
  };
  const onClearScore = (propertyKey: ClassificationPropertyKey) => {
    const next = new Map(draft);
    next.delete(propertyKey);
    setDraft(next);
    hover.setPreview(null);
    if (lastSetScore?.propertyKey === propertyKey) {
      setLastSetScore(null);
    }
  };

  const scoreByKey = draft ?? new Map(scores.map(s => [s.propertyKey, s.value]));
  const isDraftComplete = draft !== null && template.properties.every(property => draft.has(property.key));
  const isDraftIncomplete = draft !== null && !isDraftComplete;
  const previewCriticality = isDraftComplete ? lookUpCriticality(criticalityTable, draft) : null;
  const templateLevels = template.levels ?? [];
  const activeHoverPreview = updateMutation.isPending ? null : hover.preview;

  const cardSx: SxProps<Theme> = {
    mx: -2,
    p: 2,
    pb: isEditing ? 2 : 2.5,
    gap: 2,
    borderRadius: 1,
    border: "1px solid",
    borderColor: theme => (isEditing ? theme.palette.grey["300"] : "transparent"),
    backgroundColor: theme => (isEditing ? theme.palette.grey["50"] : theme.palette.background.paper),
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "left",
    ...(isInteractive && {
      "transition": "background-color 120ms ease",
      "&:hover": {
        borderColor: "transparent",
        backgroundColor: theme => theme.palette.action.hover,
      },
    }),
  };

  const cardContent = (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} sx={{ alignItems: "stretch" }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 240,
            display: "grid",
            gridTemplateColumns: "25% 1fr",
            rowGap: 1.5,
            columnGap: 2,
            alignItems: "center",
          }}
        >
          <Stack direction="row" sx={{ gap: CELL_GAP, gridColumn: 2, mb: -0.5 }}>
            {levels.map(level => (
              <Typography
                key={level}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: theme => theme.typography.pxToRem(10),
                  fontWeight: 500,
                  letterSpacing: "0.02em",
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {level}
              </Typography>
            ))}
            <Box sx={{ ml: 0.5, width: BAR_HEIGHT, flexShrink: 0 }} />
            <Box sx={{ ml: LEVEL_NAME_GAP, width: LEVEL_NAME_WIDTH, flexShrink: 0 }} />
          </Stack>
          {template.properties.map(property => (
            <PropertyRow
              key={property.key}
              property={property}
              customerCustomization={customerCustomizationByKey.get(property.key) ?? null}
              levels={levels}
              templateLevels={templateLevels}
              score={scoreByKey.get(property.key) ?? null}
              isEditing={isEditing}
              isSaving={updateMutation.isPending}
              hoverPreview={activeHoverPreview}
              onSetScore={onSetScore}
              onClearScore={onClearScore}
              onHoverPreview={hover.setPreview}
              onFocusPreview={hover.setPreviewIgnoringSuppression}
              levelExplanationBaseId={levelExplanationBaseId}
            />
          ))}
        </Box>
        <CriticalityDisplay
          savedCriticality={criticality}
          previewCriticality={previewCriticality}
          isEditing={isEditing}
          isDraftIncomplete={isDraftIncomplete}
        />
      </Stack>
      {isEditing && (
        <LevelPanel
          properties={template.properties}
          templateLevels={templateLevels}
          explainedLevel={activeHoverPreview ?? lastSetScore}
          levelExplanationBaseId={levelExplanationBaseId}
        />
      )}
      <RationaleField
        isEditing={isEditing}
        value={isEditing ? draftRationale : rationale}
        onChange={setDraftRationale}
      />
      {isEditing && (
        <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={onCancel}>
            {t("Classification.Edit.Cancel")}
          </Button>
          <Button size="small" variant="contained" loading={updateMutation.isPending} onClick={handleSave}>
            {t("Classification.Edit.Save")}
          </Button>
        </Stack>
      )}
    </>
  );

  return (
    <>
      {isInteractive ? (
        <ButtonBase onClick={onStartEdit} aria-label={t("Classification.Edit.TriggerAria")} sx={cardSx}>
          {cardContent}
        </ButtonBase>
      ) : (
        <Box sx={cardSx}>{cardContent}</Box>
      )}
      {updateMutation.isError && <DynamicForm.ErrorMessage errorMessage={t("Classification.Edit.SaveError")} />}
    </>
  );
};

const BAR_HEIGHT = 32;
const BAR_TRANSITION = "all 150ms cubic-bezier(.4, 0, .2, 1)";
const RESET_BUTTON_CLASS = "property-reset-button";
const FILL_ACTIVE_CLASS = "cell-fill-active";
const CELL_GAP = 0.5;
const LEVEL_NAME_WIDTH = 88;
const LEVEL_NAME_GAP = 1.5;
const CRITICALITY_COLUMN_WIDTH = 140;
const PREVIOUS_CRITICALITY_LINE_HEIGHT = 1.4;
const SHARED_GRID_CELL = "1 / 1";
const ANCHOR_HOVER_TO_ROW_SX = { "& > *": { pointerEvents: "none" } } as const;

type PropertyRowProps = {
  property: TemplateProperty;
  customerCustomization: string | null;
  levels: ReadonlyArray<Level>;
  templateLevels: ReadonlyArray<TemplateLevel>;
  score: Level | null;
  isEditing: boolean;
  isSaving: boolean;
  hoverPreview: PropertyLevel | null;
  onSetScore: (propertyKey: ClassificationPropertyKey, value: Level) => void;
  onClearScore: (propertyKey: ClassificationPropertyKey) => void;
  onHoverPreview: (preview: PropertyLevel | null) => void;
  onFocusPreview: (preview: PropertyLevel) => void;
  levelExplanationBaseId: string;
};

const PropertyRow: FC<PropertyRowProps> = ({
  property,
  customerCustomization,
  levels,
  templateLevels,
  score,
  isEditing,
  isSaving,
  hoverPreview,
  onSetScore,
  onClearScore,
  onHoverPreview,
  onFocusPreview,
  levelExplanationBaseId,
}) => {
  const { t } = useTranslation();
  const { anchorProps, popoverProps } = useHoverPopover();
  const max = levels[levels.length - 1];
  const hoveredLevel =
    isEditing && hoverPreview && hoverPreview.propertyKey === property.key ? hoverPreview.level : null;
  const displayScore = hoveredLevel ?? score;
  const scoredLevel = templateLevels.find(templateLevel => templateLevel.level === score);
  const shownLevel = templateLevels.find(templateLevel => templateLevel.level === displayScore);
  const explainsLevelOnHover = !isEditing && scoredLevel !== undefined;
  const closeLevelExplanation = anchorProps.onMouseLeave;
  const describeScore = () => {
    if (score === null) {
      return t("Classification.Property.AriaNotScored", { property: property.label });
    }
    if (scoredLevel === undefined) {
      return t("Classification.Property.Aria", { property: property.label, value: score, max });
    }
    return t("Classification.Property.AriaWithLevel", {
      property: property.label,
      value: score,
      max,
      levelName: scoredLevel.name,
    });
  };

  return (
    <>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(14), lineHeight: 1.4 }}>
            {property.label}
          </Typography>
          {customerCustomization && (
            <CustomerCustomizationInfo customerCustomization={customerCustomization} label={property.label} />
          )}
        </Stack>
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(10),
            lineHeight: 1.2,
            color: theme => theme.palette.text.tertiary,
          }}
        >
          {property.description}
        </Typography>
      </Box>
      <Stack
        direction="row"
        role={isEditing ? undefined : "img"}
        aria-label={isEditing ? undefined : describeScore()}
        onMouseOver={explainsLevelOnHover ? anchorProps.onMouseOver : undefined}
        onMouseLeave={isEditing ? () => onHoverPreview(null) : closeLevelExplanation}
        onClick={isEditing ? undefined : closeLevelExplanation}
        sx={[
          {
            gap: CELL_GAP,
            alignSelf: "start",
          },
          !isEditing && ANCHOR_HOVER_TO_ROW_SX,
          isEditing && {
            "&:hover": {
              [`& .${FILL_ACTIVE_CLASS}`]: { opacity: 0.9 },
              [`& .${RESET_BUTTON_CLASS}`]: { opacity: 1 },
            },
          },
        ]}
      >
        {levels.map(level => {
          const templateLevel = templateLevels.find(candidate => candidate.level === level);
          const segmentAriaLabel =
            templateLevel === undefined
              ? t("Classification.Edit.SegmentAria", { property: property.label, level })
              : t("Classification.Edit.SegmentAriaWithLevel", {
                  property: property.label,
                  level,
                  levelName: templateLevel.name,
                });
          const explanationId =
            templateLevel === undefined ? undefined : levelExplanationId(levelExplanationBaseId, property.key, level);
          return (
            <Cell
              key={level}
              propertyKey={property.key}
              level={level}
              filled={displayScore !== null && level <= displayScore}
              isEditing={isEditing}
              disabled={isSaving}
              selected={score === level}
              ariaLabel={segmentAriaLabel}
              ariaDescribedBy={explanationId}
              onSelect={() => onSetScore(property.key, level)}
              onHover={() => onHoverPreview({ propertyKey: property.key, level })}
              onFocus={() => onFocusPreview({ propertyKey: property.key, level })}
            />
          );
        })}
        {isEditing ? (
          <IconButton
            className={RESET_BUTTON_CLASS}
            size="small"
            disabled={score === null || isSaving}
            onClick={() => onClearScore(property.key)}
            aria-label={t("Classification.Edit.ClearScoreAria", { property: property.label })}
            sx={{
              ml: 0.5,
              width: BAR_HEIGHT,
              height: BAR_HEIGHT,
              opacity: 0,
              transition: "opacity 150ms",
              visibility: score === null ? "hidden" : "visible",
            }}
          >
            <XIcon />
          </IconButton>
        ) : (
          <Box sx={{ ml: 0.5, width: BAR_HEIGHT, height: BAR_HEIGHT, flexShrink: 0 }} />
        )}
        <Typography
          component="span"
          sx={{
            ml: LEVEL_NAME_GAP,
            width: LEVEL_NAME_WIDTH,
            flexShrink: 0,
            alignSelf: "center",
            fontSize: theme => theme.typography.pxToRem(14),
            lineHeight: 1.4,
            whiteSpace: "nowrap",
          }}
        >
          {shownLevel?.name ?? ""}
        </Typography>
      </Stack>
      {explainsLevelOnHover && (
        <HoverPopover {...popoverProps}>
          <Box sx={{ py: 1.5, px: 2, maxWidth: "20rem" }}>
            <LevelExplanation level={scoredLevel} question={property.question ?? ""} />
          </Box>
        </HoverPopover>
      )}
    </>
  );
};

const LevelExplanation: FC<{ level: TemplateLevel; question: string }> = ({ level, question }) => (
  <>
    <Typography sx={{ fontWeight: 500, fontSize: theme => theme.typography.pxToRem(14), lineHeight: 1.4 }}>
      {`${level.level} · ${level.title}`}
    </Typography>
    <Typography
      sx={{
        mt: 0.25,
        fontSize: theme => theme.typography.pxToRem(12),
        lineHeight: 1.35,
        color: theme => theme.palette.text.secondary,
      }}
    >
      {question}
    </Typography>
    <Typography sx={{ mt: 1, fontSize: theme => theme.typography.pxToRem(14), lineHeight: 1.45, textWrap: "pretty" }}>
      {level.description}
    </Typography>
  </>
);

const levelExplanationId = (baseId: string, propertyKey: ClassificationPropertyKey, level: Level) =>
  `${baseId}-${propertyKey}-${level}`;

const LevelPanel: FC<{
  properties: ReadonlyArray<TemplateProperty>;
  templateLevels: ReadonlyArray<TemplateLevel>;
  explainedLevel: PropertyLevel | null;
  levelExplanationBaseId: string;
}> = ({ properties, templateLevels, explainedLevel, levelExplanationBaseId }) => {
  const { t } = useTranslation();
  const isShown = (propertyKey: ClassificationPropertyKey, level: Level) =>
    explainedLevel?.propertyKey === propertyKey && explainedLevel.level === level;
  const isAnyExplanationShown = properties.some(property =>
    templateLevels.some(level => isShown(property.key, level.level))
  );
  const showsHint = !isAnyExplanationShown;
  const stackedInSharedCellSx = (isVisible: boolean) => ({
    gridArea: SHARED_GRID_CELL,
    visibility: isVisible ? "visible" : "hidden",
  });

  return (
    <Box
      sx={{
        display: "grid",
        mt: 1.5,
        py: 1.5,
        px: 2,
        borderRadius: 1,
        backgroundColor: theme => theme.palette.grey["100"],
      }}
    >
      {properties.flatMap(property =>
        templateLevels.map(level => {
          const isVisible = isShown(property.key, level.level);
          return (
            <Box
              key={`${property.key}-${level.level}`}
              id={levelExplanationId(levelExplanationBaseId, property.key, level.level)}
              aria-hidden={!isVisible}
              sx={stackedInSharedCellSx(isVisible)}
            >
              <LevelExplanation level={level} question={property.question ?? ""} />
            </Box>
          );
        })
      )}
      <Typography
        aria-hidden={!showsHint}
        sx={{
          ...stackedInSharedCellSx(showsHint),
          fontSize: theme => theme.typography.pxToRem(14),
          color: theme => theme.palette.text.secondary,
        }}
      >
        {t("Classification.Edit.LevelHint")}
      </Typography>
    </Box>
  );
};

const CustomerCustomizationInfo: FC<{ customerCustomization: string; label: string }> = ({
  customerCustomization,
  label,
}) => {
  const { t } = useTranslation();
  const { anchorProps, popoverProps } = useHoverPopover();
  return (
    <>
      <Box
        component="span"
        {...anchorProps}
        onClick={event => event.stopPropagation()}
        aria-label={t("Classification.Property.InfoAria", { property: label })}
        sx={{
          "position": "relative",
          "display": "inline-flex",
          "&:before": { content: "''", position: "absolute", inset: 0, transform: "scaleX(3.5) scaleY(2.3)" },
        }}
      >
        <InfoIcon strokeWidth={2.5} size={10} color={theme => theme.palette.primary.main} />
      </Box>
      <HoverPopover {...popoverProps}>
        <Box sx={{ py: 1.5, px: 2, maxWidth: "20rem" }}>
          <Typography variant="body2" sx={{ fontSize: theme => theme.typography.pxToRem(12), textWrap: "pretty" }}>
            {customerCustomization}
          </Typography>
        </Box>
      </HoverPopover>
    </>
  );
};

type CellProps = {
  propertyKey: ClassificationPropertyKey;
  level: Level;
  filled: boolean;
  isEditing: boolean;
  disabled: boolean;
  selected: boolean;
  ariaLabel: string;
  ariaDescribedBy: string | undefined;
  onSelect: () => void;
  onHover: () => void;
  onFocus: () => void;
};

const Cell: FC<CellProps> = ({
  propertyKey,
  level,
  filled,
  isEditing,
  disabled,
  selected,
  ariaLabel,
  ariaDescribedBy,
  onSelect,
  onHover,
  onFocus,
}) => {
  const baseSx = (theme: Theme) => ({
    position: "relative" as const,
    flex: 1,
    height: BAR_HEIGHT,
    transition: BAR_TRANSITION,
    borderRadius: `${Number(theme.shape.borderRadius) / 2}px`,
    border: `1px solid ${theme.palette.grey["300"]}`,
    backgroundColor: lighten(theme.palette.grey["100"], 0.5),
  });

  const fill = (
    <Box
      className={filled ? FILL_ACTIVE_CLASS : undefined}
      sx={theme => {
        const color = CLASSIFICATION_COLORS(theme)[propertyKey][level];
        return {
          position: "absolute",
          inset: "-1px",
          borderRadius: "inherit",
          border: `1px solid ${color.border}`,
          backgroundColor: color.fill,
          mixBlendMode: "multiply",
          opacity: filled ? 1 : 0,
          transition: filled ? BAR_TRANSITION : "opacity 600ms cubic-bezier(.05, .85, .2, 1)",
          pointerEvents: "none",
        };
      }}
    />
  );

  if (!isEditing) {
    return <Box sx={baseSx}>{fill}</Box>;
  }

  return (
    <ButtonBase
      disabled={disabled}
      onClick={onSelect}
      onMouseEnter={onHover}
      onFocus={onFocus}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-pressed={selected}
      sx={[
        baseSx,
        theme => ({
          "&:focus-visible": { outline: "none" },
          "&:focus-visible::after": {
            content: '""',
            position: "absolute",
            inset: "0px",
            borderRadius: "inherit",
            border: `2px solid ${CLASSIFICATION_COLORS(theme)[propertyKey][level].border}`,
            pointerEvents: "none",
          },
        }),
      ]}
    >
      {fill}
    </ButtonBase>
  );
};

const RationaleField: FC<{
  isEditing: boolean;
  value: string | undefined;
  onChange: (value: string) => void;
}> = ({ isEditing, value, onChange }) => {
  const { t } = useTranslation();

  if (!isEditing && !value) return null;

  return (
    <Box component="label" sx={{ display: "block", mt: 1.5 }}>
      <Typography
        component="div"
        sx={{
          fontSize: theme => theme.typography.pxToRem(10),
          fontWeight: 600,
          letterSpacing: "0.08em",
          color: theme => theme.palette.text.tertiary,
          textTransform: "uppercase",
          mb: 0.5,
        }}
      >
        {t("Classification.Edit.RationaleLabel")}
      </Typography>
      {isEditing ? (
        <TextField
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          size="small"
          placeholder={t("Classification.Edit.RationalePlaceholder")}
          value={value ?? ""}
          onChange={event => onChange(event.target.value)}
          onClick={event => event.stopPropagation()}
        />
      ) : (
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(12),
            lineHeight: 1.5,
            color: theme => theme.palette.text.primary,
            whiteSpace: "pre-wrap",
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};

const lookUpCriticality = (
  criticalityTable: ClassificationCriticalityTable,
  scoreByKey: ReadonlyMap<ClassificationPropertyKey, Level>
): Criticality | null => {
  const matchingEntry = criticalityTable.find(entry =>
    entry.scores.every(score => scoreByKey.get(score.propertyKey) === score.value)
  );
  return matchingEntry?.criticality ?? null;
};

const CriticalityDisplay: FC<{
  savedCriticality: Criticality | null | undefined;
  previewCriticality: Criticality | null;
  isEditing: boolean;
  isDraftIncomplete: boolean;
}> = ({ savedCriticality, previewCriticality, isEditing, isDraftIncomplete }) => {
  const { t } = useTranslation();
  const criticality = isEditing ? previewCriticality : savedCriticality;
  const describePreviousCriticality = () => {
    const hasNoPreviousToShow =
      !savedCriticality || !previewCriticality || savedCriticality.value === previewCriticality.value;
    if (hasNoPreviousToShow) {
      return "";
    }
    const isSameLabel = savedCriticality.labelKey === previewCriticality.labelKey;
    const previousValue = isSameLabel
      ? String(savedCriticality.value)
      : t(`Classification.Label.${savedCriticality.labelKey}`);
    return t("Classification.Criticality.Previous", { value: previousValue });
  };

  return (
    <Stack
      component="section"
      aria-label={t("Classification.Criticality.Label")}
      aria-live="polite"
      sx={{
        alignItems: "center",
        justifyContent: "center",
        mt: { xs: 2, sm: 0 },
        pr: 2.5,
        py: 1.5,
        width: { sm: CRITICALITY_COLUMN_WIDTH },
        flexShrink: 0,
        textAlign: "center",
      }}
    >
      <Typography
        sx={{
          fontSize: theme => theme.typography.pxToRem(10),
          fontWeight: 600,
          letterSpacing: "0.08em",
          color: theme => theme.palette.text.tertiary,
          textTransform: "uppercase",
        }}
      >
        {t("Classification.Criticality.Label")}
      </Typography>
      <CriticalityValue criticality={criticality ?? null} isDraftIncomplete={isDraftIncomplete} />
      <Typography
        sx={{
          mt: 0.25,
          minHeight: theme => `calc(${theme.typography.pxToRem(12)} * ${PREVIOUS_CRITICALITY_LINE_HEIGHT})`,
          fontSize: theme => theme.typography.pxToRem(12),
          lineHeight: PREVIOUS_CRITICALITY_LINE_HEIGHT,
          color: theme => theme.palette.text.tertiary,
        }}
      >
        {describePreviousCriticality()}
      </Typography>
    </Stack>
  );
};

const CriticalityValue: FC<{ criticality: Criticality | null; isDraftIncomplete: boolean }> = ({
  criticality,
  isDraftIncomplete,
}) => {
  const { t } = useTranslation();

  if (criticality) {
    return (
      <>
        <Typography
          sx={{
            fontWeight: 700,
            mt: 1,
            fontSize: theme => theme.typography.pxToRem(36),
            lineHeight: 1.1,
            color: theme => theme.palette.text.primary,
          }}
        >
          {criticality.value}
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            mt: 0.25,
            fontSize: theme => theme.typography.pxToRem(12),
            letterSpacing: "0.01em",
            color: theme => theme.palette.text.tertiary,
          }}
        >
          {t(`Classification.Label.${criticality.labelKey}`)}
        </Typography>
      </>
    );
  }

  if (isDraftIncomplete) {
    return (
      <Typography
        sx={{
          mt: 1,
          fontSize: theme => theme.typography.pxToRem(14),
          lineHeight: 1.4,
          color: theme => theme.palette.text.tertiary,
        }}
      >
        {t("Classification.Criticality.Incomplete")}
      </Typography>
    );
  }

  return (
    <Typography
      sx={{
        fontWeight: 500,
        mt: 0.5,
        fontSize: theme => theme.typography.pxToRem(20),
        lineHeight: 1.2,
        color: theme => theme.palette.text.tertiary,
      }}
    >
      –
    </Typography>
  );
};
